import { BoxHeader } from "@/components/design/BoxHeader";
import { useEscoEnabled } from "./useEscoEnabled";
import { EscoLanguageSelect } from "./EscoLanguageSelect";
import type { EscoLanguage } from "@/lib/esco";
import { useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { classificationForText, selectedClassification } from "@/lib/esco";
import { EscoSearch } from "./EscoSearch";

export function EscoEntryMapping({ kind, text, value, onChange, onTextChange }: {
  kind: "occupation" | "skill"; text: string; value: unknown;
  onChange: (value: string) => void; onTextChange?: (text: string) => void;
}) {
  const enabled = useEscoEnabled();
  const intl = useIntl();
  const id = useId();
  const [language, setLanguage] = useState<EscoLanguage>(intl.locale.startsWith("de") ? "de" : "en");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [replacing, setReplacing] = useState<string | null>(null);
  const classification = classificationForText(value, text);
  const refs = classification?.references ?? [];
  if (!enabled && !refs.length) return null;
  function showSearch(uri: string | null = null) { setReplacing(uri); setSearch(text); setOpen(true); }
  return <div className="mt-2 space-y-2">
    {refs.map(ref => <div key={ref.uri} className="flex min-w-0 items-center gap-1">
      <Button id={`${id}-mapping-${ref.uri.split("/").at(-1)}`} variant="chip" size="sm" className="min-w-0 max-w-full"
        disabled={!enabled} title={ref.label} aria-label={`${ref.label} · ESCO`} aria-expanded={open && replacing === ref.uri} aria-controls={`${id}-search-panel`}
        onClick={() => open && replacing === ref.uri ? setOpen(false) : showSearch(ref.uri)}>
        <span className="truncate">{ref.label}</span><span className="ml-1 shrink-0 text-ink-muted">· ESCO</span>
      </Button>
      <Button id={`${id}-unlink-${ref.uri.split("/").at(-1)}`} variant="icon" size="sm"
        aria-label={intl.formatMessage({ id: "esco.unlinkNamed", defaultMessage: "Unlink {name}" }, { name: ref.label })}
        onClick={() => onChange(JSON.stringify({ ...classification, references: refs.filter(r => r.uri !== ref.uri) }))}>×</Button>
      {ref.status === "PROPOSED" ? <Button id={`${id}-confirm`} variant="chip" size="sm"
        onClick={() => onChange(JSON.stringify({ ...classification, references: refs.map(r => r.uri === ref.uri
          ? { ...r, status: "CONFIRMED", source: "USER_SELECTED", basedOnText: text } : r) }))}>
        <FormattedMessage id="esco.confirm" defaultMessage="Confirm mapping" />
      </Button> : null}
    </div>)}
    {enabled && (!refs.length || (kind === "occupation" && refs.length < 5)) ? <Button id={`${id}-open`} variant="chip" size="sm" aria-expanded={open && replacing === null} aria-controls={`${id}-search-panel`}
      onClick={() => open && replacing === null ? setOpen(false) : showSearch()}>
      {refs.length ? <FormattedMessage id="esco.addOccupation" defaultMessage="Add another occupation" />
        : kind === "occupation" ? <FormattedMessage id="esco.assignOccupation" defaultMessage="Assign an occupation" />
        : <FormattedMessage id="esco.assignSkill" defaultMessage="Assign an ESCO skill" />}
    </Button> : null}
    {enabled && open ? <section id={`${id}-search-panel`} className="box-flat bg-page p-3 space-y-3"
      aria-label={kind === "occupation"
        ? intl.formatMessage({ id: "esco.chooseOccupation", defaultMessage: "Choose an occupation" })
        : intl.formatMessage({ id: "esco.chooseSkill", defaultMessage: "Choose a skill" })}
      onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); } }}>
        <BoxHeader title={kind === "occupation"
          ? <FormattedMessage id="esco.chooseOccupation" defaultMessage="Choose an occupation" />
          : <FormattedMessage id="esco.chooseSkill" defaultMessage="Choose a skill" />}
          right={<Button id={`${id}-close-search`} variant="ghost" size="sm" onClick={() => setOpen(false)}>
            <FormattedMessage id="common.close" defaultMessage="Close" />
          </Button>} />
        <div className="flex flex-wrap items-end gap-3">
        <input id={`${id}-search`} className="smycles-field-value min-w-0 flex-[1_1_14rem] rounded-md bg-surface px-3 py-2 ring-1 ring-ink-ui"
          value={search} aria-label={intl.formatMessage({ id: "esco.search", defaultMessage: "Search ESCO" })}
          placeholder={intl.formatMessage({ id: "esco.search", defaultMessage: "Search ESCO" })}
          maxLength={200} onChange={e => setSearch(e.target.value)} />
        <EscoLanguageSelect value={language} onChange={setLanguage} />
        </div>
        <p className="smycles-field-meta"><FormattedMessage id="esco.searchDisclosure" defaultMessage="Search text and language are sent to ESCO (European Commission)." /></p>
        <EscoSearch language={language} text={search} kind={kind} selected={refs.filter(r => r.status === "CONFIRMED").map(r => r.uri)} onSelect={c => {
          const label = text.trim() || c.label;
          if (!text.trim()) onTextChange?.(label);
          onChange(JSON.stringify(selectedClassification(c, label, classification ? { ...classification, references: refs.filter(r => r.uri !== replacing) } : null, kind === "occupation")));
          setOpen(false);
        }} />
        <p className="smycles-field-meta"><FormattedMessage id="esco.keepWording" defaultMessage="Your wording stays unchanged." /></p>
    </section> : null}
  </div>;
}
